import { z } from 'zod';
import { paginationQuerySchema } from './common.js';
export const IMPORT_FORMATS = ['json', 'xml', 'csv', 'xlsx', 'pdf', 'docx', 'image'];
export const importFormatSchema = z.enum(IMPORT_FORMATS);
export const IMPORT_BATCH_STATUSES = [
    'uploaded',
    'parsing',
    'needs_mapping',
    'processing',
    'ready_for_review',
    'partial',
    'completed',
    'failed',
];
export const importBatchStatusSchema = z.enum(IMPORT_BATCH_STATUSES);
export const IMPORT_RECORD_STATUSES = [
    'pending',
    'parsed',
    'needs_review',
    'invalid',
    'ready',
    'created',
    'skipped',
];
export const importRecordStatusSchema = z.enum(IMPORT_RECORD_STATUSES);
/** File extensions and MIME types the Import Centre accepts. Extensible via the importer registry. */
export const IMPORT_ACCEPT = {
    json: { extensions: ['.json'], mimeTypes: ['application/json', 'text/json'], label: 'JSON' },
    xml: { extensions: ['.xml'], mimeTypes: ['application/xml', 'text/xml'], label: 'XML / UBL' },
    csv: { extensions: ['.csv', '.tsv', '.txt'], mimeTypes: ['text/csv', 'text/plain', 'application/csv', 'text/tab-separated-values'], label: 'CSV' },
    xlsx: {
        extensions: ['.xlsx'],
        mimeTypes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
        label: 'Excel (.xlsx)',
    },
    pdf: { extensions: ['.pdf'], mimeTypes: ['application/pdf'], label: 'PDF' },
    docx: {
        extensions: ['.docx'],
        mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        label: 'Word (.docx)',
    },
    image: { extensions: ['.png', '.jpg', '.jpeg'], mimeTypes: ['image/png', 'image/jpeg'], label: 'Image (PNG, JPG)' },
};
/** Canonical fields a CSV/XLSX column can be mapped to. */
export const IMPORT_FIELDS = [
    { key: 'invoiceRef', label: 'Invoice reference / number', group: 'header', required: true, hint: 'Rows sharing this value become one invoice.' },
    { key: 'issueDate', label: 'Issue date', group: 'header', required: true },
    { key: 'dueDate', label: 'Due date', group: 'header', required: false },
    { key: 'currency', label: 'Currency', group: 'header', required: false },
    { key: 'documentType', label: 'Document type', group: 'header', required: false },
    { key: 'note', label: 'Note', group: 'header', required: false },
    { key: 'customerCode', label: 'Customer code', group: 'buyer', required: false },
    { key: 'buyerName', label: 'Customer name', group: 'buyer', required: true },
    { key: 'buyerTaxId', label: 'Customer tax ID', group: 'buyer', required: false },
    { key: 'buyerEmail', label: 'Customer email', group: 'buyer', required: false },
    { key: 'buyerPhone', label: 'Customer phone', group: 'buyer', required: false },
    { key: 'buyerStreet', label: 'Customer street', group: 'buyer', required: false },
    { key: 'buyerCity', label: 'Customer city', group: 'buyer', required: false },
    { key: 'buyerState', label: 'Customer state', group: 'buyer', required: false },
    { key: 'buyerCountry', label: 'Customer country', group: 'buyer', required: false },
    { key: 'itemCode', label: 'Item code', group: 'line', required: false },
    { key: 'itemName', label: 'Item name', group: 'line', required: true },
    { key: 'itemDescription', label: 'Item description', group: 'line', required: false },
    { key: 'lineType', label: 'Goods / service', group: 'line', required: false },
    { key: 'quantity', label: 'Quantity', group: 'line', required: true },
    { key: 'unit', label: 'Unit', group: 'line', required: false },
    { key: 'unitPrice', label: 'Unit price', group: 'line', required: true },
    { key: 'discountRate', label: 'Discount %', group: 'line', required: false },
    { key: 'discountAmount', label: 'Discount amount', group: 'line', required: false },
    { key: 'taxRate', label: 'Tax rate %', group: 'line', required: false },
    { key: 'taxCategory', label: 'Tax category code', group: 'line', required: false },
];
const fieldKeys = IMPORT_FIELDS.map((f) => f.key);
export const importMappingSchema = z.object({
    sheet: z.string().max(200).optional(),
    headerRow: z.number().int().min(1).max(1000).default(1),
    /** invoiceRef groups rows by the mapped reference column; row treats each row as its own invoice. */
    groupBy: z.enum(['invoiceRef', 'row']).default('invoiceRef'),
    columns: z.partialRecord(z.enum(fieldKeys), z.string().max(300).nullable()).default({}),
    defaults: z
        .object({
        currency: z.string().length(3).optional(),
        taxRate: z.string().max(10).optional(),
        taxCategory: z.string().max(40).optional(),
        lineType: z.enum(['goods', 'service']).optional(),
        unit: z.string().max(20).optional(),
        dateFormat: z.enum(['auto', 'DMY', 'MDY', 'YMD']).default('auto'),
    })
        .default({ dateFormat: 'auto' }),
});
export const importOptionsSchema = z.object({
    createMissingCustomers: z.boolean().default(false),
});
export const uploadImportFieldsSchema = z.object({
    taxpayerProfileId: z.uuid(),
    createMissingCustomers: z.stringbool().optional(),
});
export const importListQuerySchema = paginationQuerySchema.extend({
    taxpayerProfileId: z.uuid().optional(),
    status: importBatchStatusSchema.optional(),
});
/** Records default to file order (ascending), unlike batches which list newest first. */
export const importRecordsQuerySchema = paginationQuerySchema.extend({
    order: z.enum(['asc', 'desc']).default('asc'),
    status: importRecordStatusSchema.optional(),
});
/** Normalised candidate invoice produced by every importer (loose by design — it is untrusted). */
export const importedLineSchema = z.object({
    itemCode: z.string().nullish(),
    itemName: z.string().nullish(),
    itemDescription: z.string().nullish(),
    lineType: z.string().nullish(),
    quantity: z.union([z.string(), z.number()]).nullish(),
    unit: z.string().nullish(),
    unitPrice: z.union([z.string(), z.number()]).nullish(),
    discountRate: z.union([z.string(), z.number()]).nullish(),
    discountAmount: z.union([z.string(), z.number()]).nullish(),
    taxRate: z.union([z.string(), z.number()]).nullish(),
    taxCategory: z.string().nullish(),
});
export const importedInvoiceSchema = z.object({
    externalReference: z.string().nullish(),
    /** 1-based row numbers in the source spreadsheet this invoice was assembled from. */
    sourceRows: z.array(z.number().int()).nullish(),
    documentType: z.string().nullish(),
    issueDate: z.string().nullish(),
    dueDate: z.string().nullish(),
    currency: z.string().nullish(),
    note: z.string().nullish(),
    customerCode: z.string().nullish(),
    buyer: z
        .object({
        legalName: z.string().nullish(),
        taxIdentifier: z.string().nullish(),
        email: z.string().nullish(),
        phone: z.string().nullish(),
        streetName: z.string().nullish(),
        cityName: z.string().nullish(),
        stateCode: z.string().nullish(),
        countryCode: z.string().nullish(),
    })
        .default({}),
    lines: z.array(importedLineSchema).default([]),
    declaredTotals: z
        .object({
        taxExclusive: z.union([z.string(), z.number()]).nullish(),
        taxTotal: z.union([z.string(), z.number()]).nullish(),
        payable: z.union([z.string(), z.number()]).nullish(),
    })
        .nullish(),
});
export const updateImportRecordSchema = z.object({
    /** Corrected values; the record is re-validated on save. */
    extractedData: importedInvoiceSchema.optional(),
    /**
     * `accept` marks a reviewed record ready for draft creation (only if it validates),
     * `skip` excludes it, `restore` puts a skipped record back into review.
     */
    action: z.enum(['accept', 'skip', 'restore']).optional(),
});
export const commitImportSchema = z.object({
    /** Only create drafts for these records; omit for every ready record. */
    recordIds: z.array(z.uuid()).max(5000).optional(),
});
// ───────────── responses ─────────────
export const importValidationIssueSchema = z.object({
    path: z.string(),
    code: z.string(),
    message: z.string(),
    severity: z.enum(['error', 'warning']),
});
export const importAnalysisSchema = z.object({
    sheets: z
        .array(z.object({
        name: z.string(),
        rowCount: z.number(),
        headerRow: z.number(),
        headers: z.array(z.string()),
        sampleRows: z.array(z.array(z.string())),
    }))
        .optional(),
    suggestedMapping: importMappingSchema.optional(),
    structure: z.string().optional(),
    extraction: z
        .object({
        provider: z.string(),
        simulated: z.boolean(),
        textExtraction: z.boolean(),
        ocr: z.boolean(),
        structuredExtraction: z.boolean(),
        notes: z.array(z.string()),
    })
        .optional(),
    rawTextPreview: z.string().optional(),
});
export const importBatchSchema = z.object({
    id: z.uuid(),
    taxpayerProfileId: z.uuid(),
    originalFilename: z.string(),
    mimeType: z.string(),
    fileSize: z.number(),
    sourceFormat: importFormatSchema,
    status: importBatchStatusSchema,
    statusMessage: z.string().nullable(),
    analysis: importAnalysisSchema.nullable(),
    mapping: importMappingSchema.nullable(),
    options: importOptionsSchema.nullable(),
    totalRecords: z.number(),
    acceptedRecords: z.number(),
    rejectedRecords: z.number(),
    createdRecords: z.number(),
    uploadedByName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    completedAt: z.string().nullable(),
});
export const importRecordSchema = z.object({
    id: z.uuid(),
    recordNumber: z.number(),
    externalReference: z.string().nullable(),
    status: importRecordStatusSchema,
    validationErrors: z.array(importValidationIssueSchema).nullable(),
    extractedData: importedInvoiceSchema.nullable(),
    confidenceData: z.record(z.string(), z.number()).nullable(),
    invoiceId: z.uuid().nullable(),
    invoiceNumber: z.string().nullable(),
    summary: z.object({
        buyerName: z.string().nullable(),
        issueDate: z.string().nullable(),
        lineCount: z.number(),
        total: z.string().nullable(),
        currency: z.string().nullable(),
    }),
});
export const importCapabilitiesSchema = z.object({
    formats: z.array(z.object({
        format: importFormatSchema,
        label: z.string(),
        extensions: z.array(z.string()),
        /** What actually works in this deployment, in plain language. */
        support: z.enum(['full', 'text_only', 'manual_only']),
        note: z.string().nullable(),
    })),
    maxFileBytes: z.number(),
    maxRecords: z.number(),
    extraction: z.object({
        provider: z.string(),
        simulated: z.boolean(),
        textExtraction: z.boolean(),
        ocr: z.boolean(),
        structuredExtraction: z.boolean(),
        notes: z.array(z.string()),
    }),
    unsupported: z.array(z.object({ extension: z.string(), reason: z.string() })),
});
//# sourceMappingURL=imports.js.map